/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "react-query";
import {
  doc,
  getDocs,
  getDoc,
  collection,
  query,
  where,
  orderBy,
  addDoc,
  deleteDoc,
  updateDoc,
  Timestamp,
  arrayUnion,
} from "firebase/firestore";
import { db, createQuery } from "../providers/database";
import { collectionNames } from "./dictionary/collectionNames";
import { isUsableAvatarUrl, timeAgo } from "../utils/communityHub";

const HUBS = collectionNames.communityHub;
const MEMBERS_SUB = "HubMembers";
const MODERATORS_SUB = "HubModerators";
const POSTS_SUB = "Posts";
const COMMENTS_SUB = "Comments";
const USER_COMMUNITY_HUBS_FIELD = "cummunityHub";

export type HubPerson = {
  email: string;
  name: string;
  photoURL?: string;
};

export type CommunityHub = {
  id: string;
  hubName: string;
  location?: string;
  descriptions?: string;
  moderatorBy?: HubPerson;
  memberCount?: number;
  hubMembers?: number;
};

export type HubMemberDoc = HubPerson & { id: string };
export type HubModeratorDoc = HubPerson & { id: string };

export type PostAuthor = {
  name: string;
  avatarUrl?: string;
  isModerator?: boolean;
};

export type CommunityHubPost = {
  id: string;
  hubId?: string;
  title: string;
  body: string;
  images: string[];
  createdAt?: Timestamp;
  author: PostAuthor;
  authorName: string;
  authorInitials: string;
  authorIsModerator: boolean;
  authorAvatarUrl?: string;
};

export type CommunityHubComment = {
  id: string;
  postId?: string;
  text: string;
  createdAt?: Timestamp;
  author: HubPerson;
  authorName: string;
  authorInitials: string;
  authorAvatarUrl?: string;
};

export function getHubName(hub?: CommunityHub | null) {
  return hub?.hubName || "";
}

/** All hubs, or filtered by hub name list (seeker community membership). */
export function useCommunityHub(hubNames?: string[]) {
  return useQuery(
    ["communityHub", hubNames],
    createQuery(() => {
      if (hubNames && hubNames.length > 0) {
        return query(
          collection(db, HUBS),
          where("hubName", "in", hubNames),
        );
      }
      return query(collection(db, HUBS), orderBy("hubName", "asc"));
    }),
    { enabled: hubNames === undefined || hubNames.length > 0 },
  );
}

export function getHubDescription(hub?: CommunityHub | null) {
  return hub?.descriptions || "";
}

export function getHubInitials(hub?: CommunityHub | null) {
  return getInitials(getHubName(hub));
}

export function getInitials(name: string) {
  return (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("");
}

/** First letter of each word — used for community hub logo badges. */
export function getHubLogoInitials(name: string) {
  return (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() || "")
    .join("");
}

/** Scale font so long initials stay inside a square hub logo. */
export function getHubLogoFontSize(initials: string, size: number) {
  const len = initials.length || 1;
  const maxFont = size * 0.38;
  const estimatedWidth = len * maxFont * 0.65;
  const available = size * 0.85;
  if (estimatedWidth <= available) return maxFont;
  return Math.floor(available / (len * 0.65));
}

export function normalizeEmail(email?: string) {
  return (email || "").trim().toLowerCase();
}

const ADMIN_FALLBACK_DISPLAY_NAME = "Admin";

const INVALID_AUTHOR_NAMES = new Set([
  "Anonymous",
  "User",
  ADMIN_FALLBACK_DISPLAY_NAME,
  "Moderator",
  "Unknown",
]);

export function isValidAuthorDisplayName(value?: string) {
  const trimmed = String(value || "").trim();
  return !!trimmed && !INVALID_AUTHOR_NAMES.has(trimmed);
}

/** Name from a Users doc or merged auth profile (same fields seeker auth uses). */
export function getUserRecordDisplayName(record?: any) {
  if (!record) return "";
  const candidates = [
    record.preferredName,
    record.name,
    record.displayName,
  ];
  for (const value of candidates) {
    const trimmed = String(value || "").trim();
    if (isValidAuthorDisplayName(trimmed)) return trimmed;
  }
  return "";
}

/** Resolve display name for posts/comments (auth user + optional Firestore profile). */
export function getCommunityAuthorName(user?: any, profile?: any) {
  const resolved =
    getUserRecordDisplayName(profile) ||
    getUserRecordDisplayName(user) ||
    String(user?.displayName || "").trim();
  if (isValidAuthorDisplayName(resolved)) return resolved;
  return ADMIN_FALLBACK_DISPLAY_NAME;
}

/** Resolve comment author name from Users collection by email (admin feed). */
export async function resolveCommentAuthorName(author?: {
  name?: string;
  email?: string;
}) {
  const email = author?.email?.trim();
  if (email) {
    try {
      const dbUser = await lookupUserByEmail(email);
      const dbName = getUserRecordDisplayName(dbUser);
      if (dbName) return dbName;
    } catch {
      // fall through to stored name
    }
  }
  const stored = String(author?.name || "").trim();
  if (isValidAuthorDisplayName(stored)) return stored;
  return ADMIN_FALLBACK_DISPLAY_NAME;
}

/** Resolve name when posting a comment (loads Users doc if needed). */
export async function resolveAuthorNameForPost(user?: any, profile?: any) {
  const fromMerged =
    getUserRecordDisplayName(profile) || getUserRecordDisplayName(user);
  if (fromMerged) return fromMerged;

  const email = profile?.email || user?.email;
  if (email) {
    try {
      const dbUser = await lookupUserByEmail(email);
      const dbName = getUserRecordDisplayName(dbUser);
      if (dbName) return dbName;
    } catch {
      // ignore
    }
  }

  const displayName = String(user?.displayName || user?.name || "").trim();
  if (isValidAuthorDisplayName(displayName)) return displayName;
  return ADMIN_FALLBACK_DISPLAY_NAME;
}

export function formatTimeAgo(ts?: Timestamp | { seconds?: number } | null) {
  if (!ts) return "";
  const seconds =
    typeof (ts as Timestamp).toMillis === "function"
      ? Math.floor((ts as Timestamp).toMillis() / 1000)
      : (ts as { seconds?: number }).seconds;
  if (seconds) return timeAgo(seconds);
  return "";
}

export function isSuperAdmin(role?: string) {
  return role === "sadmin";
}

/** Admin, Super Admin (sadmin), and Talent Acquisition Coordinator (tac) only */
export function isModeratorRole(role?: string) {
  return role === "admin" || role === "sadmin" || role === "tac";
}

export function isSeekerRole(role?: string) {
  return role === "SKR" || role === "COMM";
}

/** Admins and seekers can be assigned as hub moderators from the admin panel. */
export function canBeAssignedAsHubModerator(role?: string) {
  return isModeratorRole(role) || isSeekerRole(role);
}

export function userCanModerateHub(
  hub: CommunityHub | null | undefined,
  userEmail?: string,
  role?: string,
  extraModeratorEmails: string[] = [],
) {
  if (!hub) return false;
  if (isSuperAdmin(role)) return true;
  const email = normalizeEmail(userEmail);
  if (!email) return false;
  if (normalizeEmail(hub.moderatorBy?.email) === email) return true;
  return extraModeratorEmails.some((e) => normalizeEmail(e) === email);
}

/** Seeker-side Ambassador = hub moderator (HubModerators or primary moderatorBy). */
export function userIsHubAmbassador(
  hub: CommunityHub | null | undefined,
  userEmail?: string,
  extraModeratorEmails: string[] = [],
) {
  if (!hub) return false;
  const email = normalizeEmail(userEmail);
  if (!email) return false;
  if (normalizeEmail(hub.moderatorBy?.email) === email) return true;
  return extraModeratorEmails.some((e) => normalizeEmail(e) === email);
}

async function fetchCommunityHubsByIds(ids: string[]) {
  if (ids.length === 0) return [];
  const hubs = await Promise.all(
    ids.map(async (id) => {
      const snap = await getDoc(doc(db, HUBS, id));
      if (!snap.exists()) return null;
      const membersSnap = await getDocs(
        collection(db, HUBS, id, MEMBERS_SUB),
      );
      return {
        id: snap.id,
        ...snap.data(),
        memberCount: membersSnap.size,
        hubMembers: membersSnap.size,
      } as CommunityHub;
    }),
  );
  return hubs.filter(Boolean) as CommunityHub[];
}

export function useMemberHubIds(userEmail?: string) {
  return useQuery(
    ["memberHubIds", userEmail],
    () => getHubIdsForMemberEmail(userEmail!),
    { enabled: !!userEmail },
  );
}

export function useMemberHubs(userEmail?: string) {
  return useQuery(
    ["memberHubs", userEmail],
    async () => fetchCommunityHubsByIds(await getHubIdsForMemberEmail(userEmail!)),
    { enabled: !!userEmail },
  );
}

export function useAmbassadorHubIds(userEmail?: string) {
  return useQuery(
    ["ambassadorHubIds", userEmail],
    () => getHubIdsForModeratorEmail(userEmail!),
    { enabled: !!userEmail },
  );
}

export function useAmbassadorHubs(userEmail?: string) {
  return useQuery(
    ["ambassadorHubs", userEmail],
    async () => fetchCommunityHubsByIds(await getHubIdsForModeratorEmail(userEmail!)),
    { enabled: !!userEmail },
  );
}

export function useAdminCommunityHubs(options?: {
  memberEmail?: string;
  moderatorEmail?: string;
  adminView?: boolean;
}) {
  const { memberEmail, moderatorEmail, adminView } = options || {};
  return useQuery(
    ["communityHub", "admin", { memberEmail, moderatorEmail, adminView }],
    async () => {
      const snap = await getDocs(
        query(collection(db, HUBS), orderBy("hubName", "asc")),
      );
      let hubs = await Promise.all(
        snap.docs.map(async (d) => {
          const data = d.data();
          const membersSnap = await getDocs(
            collection(db, HUBS, d.id, MEMBERS_SUB),
          );
          return {
            id: d.id,
            ...data,
            memberCount: membersSnap.size,
            hubMembers: membersSnap.size,
          } as CommunityHub;
        }),
      );
      if (adminView) return hubs;
      if (memberEmail) {
        const allowed = await getHubIdsForMemberEmail(memberEmail);
        hubs = hubs.filter((h) => allowed.includes(h.id));
      }
      if (moderatorEmail) {
        const allowed = await getHubIdsForModeratorEmail(moderatorEmail);
        hubs = hubs.filter((h) => allowed.includes(h.id));
      }
      return hubs;
    },
  );
}

// Note: these scan each hub's subcollection directly instead of using a
// collectionGroup query. CollectionGroup queries require a dedicated index
// (which may be missing) and would silently return nothing — scanning hubs is
// index-free and guarantees correct membership/moderator detection.
async function getHubIdsForMemberEmail(email: string) {
  const normalized = normalizeEmail(email);
  if (!normalized) return [];
  const hubsSnap = await getDocs(collection(db, HUBS));
  const ids: string[] = [];
  await Promise.all(
    hubsSnap.docs.map(async (h) => {
      const memSnap = await getDocs(
        query(
          collection(db, HUBS, h.id, MEMBERS_SUB),
          where("email", "==", normalized),
        ),
      );
      if (!memSnap.empty) ids.push(h.id);
    }),
  );
  return Array.from(new Set(ids));
}

async function getHubIdsForModeratorEmail(email: string) {
  const normalized = normalizeEmail(email);
  if (!normalized) return [];
  const hubsSnap = await getDocs(collection(db, HUBS));
  const ids: string[] = [];
  await Promise.all(
    hubsSnap.docs.map(async (h) => {
      if (normalizeEmail(h.data()?.moderatorBy?.email) === normalized) {
        ids.push(h.id);
        return;
      }
      const modSnap = await getDocs(
        query(
          collection(db, HUBS, h.id, MODERATORS_SUB),
          where("email", "==", normalized),
        ),
      );
      if (!modSnap.empty) ids.push(h.id);
    }),
  );
  return Array.from(new Set(ids));
}

export function useCommunityHubById(hubId: string) {
  return useQuery(
    ["communityHub", hubId],
    async () => {
      if (!hubId) return null;
      const snap = await getDoc(doc(db, HUBS, hubId));
      if (!snap.exists()) return null;
      const membersSnap = await getDocs(
        collection(db, HUBS, hubId, MEMBERS_SUB),
      );
      return {
        id: snap.id,
        ...snap.data(),
        memberCount: membersSnap.size,
        hubMembers: membersSnap.size,
      } as CommunityHub;
    },
    { enabled: !!hubId },
  );
}

export function useHubMembers(hubId: string) {
  return useQuery(
    ["hubMembers", hubId],
    createQuery(() => query(collection(db, HUBS, hubId, MEMBERS_SUB))),
    { enabled: !!hubId },
  );
}

export function useHubModerators(hubId: string) {
  return useQuery(
    ["hubModerators", hubId],
    async () => {
      // Note: do NOT use Firestore orderBy("name") here — Firestore excludes
      // documents that are missing the ordered field, which would hide
      // moderators saved without a name. Fetch all, then sort client-side.
      const snap = await getDocs(collection(db, HUBS, hubId, MODERATORS_SUB));
      return snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a: any, b: any) =>
          String(a.name || a.email || "").localeCompare(
            String(b.name || b.email || ""),
          ),
        );
    },
    { enabled: !!hubId },
  );
}

/** Milliseconds since epoch for a hub post's createdAt (Firestore Timestamp or legacy shapes). */
export function getPostCreatedAtMs(post: { createdAt?: unknown }) {
  const ts = post?.createdAt as
    | { toMillis?: () => number; seconds?: number; _seconds?: number }
    | undefined;
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (typeof ts.seconds === "number") return ts.seconds * 1000;
  if (typeof ts._seconds === "number") return ts._seconds * 1000;
  return 0;
}

/** Newest first by createdAt. */
export function sortHubPostsNewestFirst(posts: any[] | undefined) {
  return [...(posts || [])].sort(
    (a, b) => getPostCreatedAtMs(b) - getPostCreatedAtMs(a),
  );
}

export function useHubPosts(hubId: string) {
  return useQuery(
    ["hubPosts", hubId],
    createQuery(() =>
      query(collection(db, HUBS, hubId, POSTS_SUB), orderBy("createdAt", "desc")),
    ),
    {
      enabled: !!hubId,
      select: (posts) => sortHubPostsNewestFirst(posts),
    },
  );
}

export function useHubPostComments(hubId: string, hubPostId: string) {
  return useQuery(
    ["hubPostComments", hubPostId, hubId],
    createQuery(() =>
      query(
        collection(db, HUBS, hubId, POSTS_SUB, hubPostId, COMMENTS_SUB),
        orderBy("createdAt", "asc"),
      ),
    ),
    { enabled: !!hubId && !!hubPostId },
  );
}

export async function lookupUserByEmail(email: string) {
  const normalized = normalizeEmail(email);
  const snap = await getDocs(
    query(collection(db, collectionNames.users), where("email", "==", normalized)),
  );
  if (snap.empty) {
    const snapOriginal = await getDocs(
      query(collection(db, collectionNames.users), where("email", "==", email.trim())),
    );
    if (snapOriginal.empty) return null;
    const docSnap = snapOriginal.docs[0];
    return { id: docSnap.id, ...docSnap.data() };
  }
  const docSnap = snap.docs[0];
  return { id: docSnap.id, ...docSnap.data() };
}

export function useCreateCommunityHub() {
  const qc = useQueryClient();
  return useMutation(
    async (input: {
      hubName: string;
      location: string;
      descriptions: string;
      moderatorBy: HubPerson;
    }) => {
      const ref = await addDoc(collection(db, HUBS), {
        hubName: input.hubName.trim(),
        location: input.location.trim(),
        descriptions: input.descriptions.trim(),
        moderatorBy: {
          email: normalizeEmail(input.moderatorBy.email),
          name: input.moderatorBy.name.trim(),
        },
      });
      return ref.id;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(["communityHub"]);
      },
    },
  );
}

export async function addHubMember(hubId: string, person: HubPerson) {
  const email = normalizeEmail(person.email);
  const existing = await getDocs(
    query(collection(db, HUBS, hubId, MEMBERS_SUB), where("email", "==", email)),
  );
  if (!existing.empty) return false;
  const photoURL = isUsableAvatarUrl(person.photoURL)
    ? String(person.photoURL).trim()
    : undefined;
  await addDoc(collection(db, HUBS, hubId, MEMBERS_SUB), {
    email,
    name: person.name.trim(),
    ...(photoURL ? { photoURL } : {}),
  });

  try {
    const hubSnap = await getDoc(doc(db, HUBS, hubId));
    const hubName = String(hubSnap.data()?.hubName || "").trim();
    if (hubName) {
      const user = await lookupUserByEmail(email);
      if (user?.id) {
        await updateDoc(doc(db, collectionNames.users, user.id), {
          [USER_COMMUNITY_HUBS_FIELD]: arrayUnion(hubName),
        });
      }
    }
  } catch {
    // Member was added; user doc sync is best-effort
  }

  return true;
}

export async function removeHubMember(hubId: string, memberDocId: string) {
  await deleteDoc(doc(db, HUBS, hubId, MEMBERS_SUB, memberDocId));
}

export async function isHubMemberByEmail(hubId: string, email: string) {
  const snap = await getDocs(
    query(
      collection(db, HUBS, hubId, MEMBERS_SUB),
      where("email", "==", normalizeEmail(email)),
    ),
  );
  return !snap.empty;
}

export async function addHubModerator(hubId: string, person: HubPerson) {
  const email = normalizeEmail(person.email);
  const hubSnap = await getDoc(doc(db, HUBS, hubId));
  if (normalizeEmail(hubSnap.data()?.moderatorBy?.email) === email) return;
  const existing = await getDocs(
    query(collection(db, HUBS, hubId, MODERATORS_SUB), where("email", "==", email)),
  );
  if (!existing.empty) return;
  // Always store a non-empty name so the moderator reliably renders.
  const name = (person.name || "").trim() || email;
  await addDoc(collection(db, HUBS, hubId, MODERATORS_SUB), {
    email,
    name,
  });
}

export async function removeHubModerator(hubId: string, moderatorDocId: string) {
  await deleteDoc(doc(db, HUBS, hubId, MODERATORS_SUB, moderatorDocId));
}

export async function postComment(
  hubId: string,
  postId: string,
  commentData: {
    text: string;
    author: HubPerson;
  },
) {
  return addDoc(collection(db, HUBS, hubId, POSTS_SUB, postId, COMMENTS_SUB), {
    text: commentData.text,
    author: {
      email: normalizeEmail(commentData.author.email),
      name: commentData.author.name,
      ...(commentData.author.photoURL
        ? { photoURL: commentData.author.photoURL }
        : {}),
    },
    createdAt: Timestamp.now(),
  });
}

export async function updateComment(
  hubId: string,
  postId: string,
  commentId: string,
  newText: string,
) {
  return updateDoc(
    doc(db, HUBS, hubId, POSTS_SUB, postId, COMMENTS_SUB, commentId),
    { text: newText },
  );
}

export async function deleteComment(
  hubId: string,
  postId: string,
  commentId: string,
) {
  return deleteDoc(
    doc(db, HUBS, hubId, POSTS_SUB, postId, COMMENTS_SUB, commentId),
  );
}

export async function createHubPost(
  hubId: string,
  data: {
    authorName: string;
    authorAvatarUrl?: string;
    authorIsModerator: boolean;
    title: string;
    body: string;
    images: string[];
  },
) {
  return addDoc(collection(db, HUBS, hubId, POSTS_SUB), {
    author: {
      name: data.authorName.trim(),
      ...(data.authorAvatarUrl ? { avatarUrl: data.authorAvatarUrl } : {}),
      isModerator: data.authorIsModerator,
    },
    title: data.title.trim(),
    body: data.body.trim(),
    images: data.images || [],
    createdAt: Timestamp.now(),
  });
}

export async function updateHubPost(
  hubId: string,
  postId: string,
  data: { title?: string; body?: string; images?: string[] },
) {
  return updateDoc(doc(db, HUBS, hubId, POSTS_SUB, postId), data);
}

export async function deleteHubPost(hubId: string, postId: string) {
  const commentsSnap = await getDocs(
    collection(db, HUBS, hubId, POSTS_SUB, postId, COMMENTS_SUB),
  );
  await Promise.all(
    commentsSnap.docs.map((c) =>
      deleteDoc(doc(db, HUBS, hubId, POSTS_SUB, postId, COMMENTS_SUB, c.id)),
    ),
  );
  await deleteDoc(doc(db, HUBS, hubId, POSTS_SUB, postId));
}

export function useCreateCommunityPost() {
  const qc = useQueryClient();
  return useMutation(
    async (input: {
      hubId: string;
      authorName: string;
      authorAvatarUrl?: string;
      authorIsModerator: boolean;
      title: string;
      body: string;
      images: string[];
    }) => {
      const ref = await createHubPost(input.hubId, {
        authorName: input.authorName,
        authorAvatarUrl: input.authorAvatarUrl,
        authorIsModerator: input.authorIsModerator,
        title: input.title,
        body: input.body,
        images: input.images,
      });
      return ref.id;
    },
    {
      onSuccess: (_, { hubId }) => {
        qc.invalidateQueries(["hubPosts", hubId]);
      },
    },
  );
}

export function useUpdateCommunityPost() {
  const qc = useQueryClient();
  return useMutation(
    async (input: {
      hubId: string;
      postId: string;
      title?: string;
      body?: string;
      images?: string[];
    }) => {
      const { hubId, postId, ...rest } = input;
      await updateHubPost(hubId, postId, rest);
    },
    {
      onSuccess: (_, { hubId }) => {
        qc.invalidateQueries(["hubPosts", hubId]);
      },
    },
  );
}

export function useDeleteCommunityPost() {
  const qc = useQueryClient();
  return useMutation(
    async ({ hubId, postId }: { hubId: string; postId: string }) => {
      await deleteHubPost(hubId, postId);
    },
    {
      onSuccess: (_, { hubId }) => {
        qc.invalidateQueries(["hubPosts", hubId]);
      },
    },
  );
}

export function useCreateCommunityComment() {
  const qc = useQueryClient();
  return useMutation(
    async (input: {
      hubId: string;
      postId: string;
      author: HubPerson;
      text: string;
    }) => {
      await postComment(input.hubId, input.postId, {
        text: input.text.trim(),
        author: input.author,
      });
    },
    {
      onSuccess: (_, { hubId, postId }) => {
        qc.invalidateQueries(["hubPostComments", postId, hubId]);
      },
    },
  );
}

export function useUpdateCommunityComment() {
  const qc = useQueryClient();
  return useMutation(
    async (input: {
      hubId: string;
      postId: string;
      commentId: string;
      text: string;
    }) => {
      await updateComment(
        input.hubId,
        input.postId,
        input.commentId,
        input.text.trim(),
      );
    },
    {
      onSuccess: (_, { hubId, postId }) => {
        qc.invalidateQueries(["hubPostComments", postId, hubId]);
      },
    },
  );
}

export function useDeleteCommunityComment() {
  const qc = useQueryClient();
  return useMutation(
    async (input: { hubId: string; postId: string; commentId: string }) => {
      await deleteComment(input.hubId, input.postId, input.commentId);
    },
    {
      onSuccess: (_, { hubId, postId }) => {
        qc.invalidateQueries(["hubPostComments", postId, hubId]);
      },
    },
  );
}

/** @deprecated Use `useAdminCommunityHubs` — alias from legacy `communityHubs` module. */
export const useCommunityHubs = useAdminCommunityHubs;

/** Legacy names from `communityHubs.ts` (same hooks; use `useCommunityHubById` for a single hub). */
export {
  useHubMembers as useCommunityHubMembers,
  useHubModerators as useCommunityHubModerators,
  useHubPosts as useCommunityHubPosts,
  useHubPostComments as useCommunityHubComments,
};
